import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Autoplay, Pagination } from "swiper/modules";

export default function Hero() {
  const [showSlider, setShowSlider] = useState(false);

  // Start with video, then show slider after 8s
  useEffect(() => {
    if (!showSlider) {
      const timer = setTimeout(() => setShowSlider(true), 8000);
      return () => clearTimeout(timer);
    }
  }, [showSlider]);

  const slides = [
    {
      image: "/images/ezrahavenue.jpg",
      headline: "Ezrah",
      text: "Discover world-class mountain living with ski-in, ski-out access.",
      button: { text: "View Properties", link: "/projects" },
    },
    {
      image: "/images/luxeheights.jpg",
      headline: "Luxe Heights Estates",
      text: "Elegant design blended with breathtaking Rocky Mountain views.",
      button: { text: "View Properties", link: "/projects" },
    },
    {
      image: "/images/thehavern.jpg",
      headline: "The Havern",
      text: "Live in the heart of Aspen with unmatched luxury and convenience.",
      button: { text: "View Properties", link: "/projects" },
    },
  ];

  // Loop back to video after slider ends
  const handleSlideChange = (swiper) => {
    if (swiper.isEnd) {
      setTimeout(() => setShowSlider(false), 6000);
    }
  };

  return (
    <section
      id="home"
      className="relative w-full h-screen flex items-center justify-center text-center overflow-hidden"
    >
      {/* Video Intro */}
      <AnimatePresence>
        {!showSlider && (
          <motion.video
            key="intro-video"
            className="absolute inset-0 w-full h-full object-cover"
            src="/hero-vid.mp4"
            autoPlay
            loop
            muted
            playsInline
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
          />
        )}
      </AnimatePresence>

      {/* Vertical Slider */}
      <AnimatePresence>
        {showSlider && (
          <motion.div
            key="hero-slider"
            className="absolute inset-0 w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
          >
            <Swiper
              modules={[Pagination, Autoplay]}
              direction="vertical"
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              loop={false}
              pagination={{
                clickable: true,
                renderBullet: (index, className) => {
                  return `
                    <div class="${className} bullet-wrapper flex flex-col items-center gap-2 font-inter relative">
                      <span class="number text-xs tracking-wider">${String(
                        index + 1
                      ).padStart(2, "0")}</span>
                      <span class="line-container relative w-px h-16 bg-white/20 overflow-hidden">
                        <span class="progress-bar absolute bottom-0 left-0 w-full"></span>
                      </span>
                    </div>
                  `;
                },
              }}
              onSlideChange={handleSlideChange}
              className="h-full"
            >
              {slides.map((slide, index) => (
                <SwiperSlide key={index}>
                  {/* Background with parallax zoom */}
                  <motion.div
                    className="absolute inset-0 w-full h-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${slide.image})` }}
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{
                      duration: 5,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  />

                  {/* Slide Content */}
                  <div className="relative z-10 h-full flex flex-col items-center justify-center px-6">
                    <motion.h1
                      key={slide.headline}
                      initial={{ opacity: 0, y: 60 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -40 }}
                      transition={{
                        duration: 1,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      className="font-bold text-white tracking-wide drop-shadow-lg text-5xl md:text-7xl font-poppins"
                    >
                      {slide.headline}
                    </motion.h1>
                    <motion.p
                      key={slide.text}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{
                        delay: 0.2,
                        duration: 1,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      className="mt-6 text-lg md:text-xl text-white max-w-2xl mx-auto drop-shadow-md font-inter"
                    >
                      {slide.text}
                    </motion.p>
                    <motion.a
                      key={slide.button.text}
                      href={slide.button.link}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{
                        delay: 0.4,
                        duration: 1,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-block mt-8 bg-white text-gray-900 px-8 py-3 rounded-md text-lg font-semibold hover:bg-gray-100 transition font-inter"
                    >
                      {slide.button.text}
                    </motion.a>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Pagination + Title */}
            <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 flex flex-col items-center gap-8 z-20">
              <motion.h3
                key="properties-title"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                className="text-white text-sm font-light tracking-widest uppercase rotate-90 origin-center font-inter"
              >
                Properties
              </motion.h3>
              <div className="swiper-pagination custom-pagination flex flex-col gap-6"></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-1"
        >
          <div className="w-1 h-2 bg-white rounded-full"></div>
        </motion.div>
      </div>

      {/* Custom Pagination Styling */}
      <style jsx global>{`
        /* Kill default dots */
        .swiper-pagination-bullet {
          width: auto !important;
          height: auto !important;
          background: none !important;
          border-radius: 0 !important;
          margin: 12px 0 !important;
          opacity: 0.6;
          transform: translateY(10px);
          transition: transform 0.6s ease, opacity 0.6s ease;
        }

        .swiper-pagination-bullet-active {
          transform: translateY(0);
          opacity: 1;
        }

        .swiper-pagination-bullet .number {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.75rem;
          transition: color 0.3s ease;
        }
        .swiper-pagination-bullet-active .number {
          color: #ffffff;
          font-weight: 600;
        }

        .swiper-pagination-bullet .line-container {
          width: 2px;
          height: 64px;
          background-color: rgba(255, 255, 255, 0.2);
          position: relative;
          overflow: hidden;
        }

        .swiper-pagination-bullet .progress-bar {
          background-color: #1e3a8a;
          width: 100%;
          height: 0%;
          position: absolute;
          bottom: 0;
          left: 0;
        }
        .swiper-pagination-bullet-active .progress-bar {
          animation: fillProgress 5s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
        }

        @keyframes fillProgress {
          from {
            height: 0%;
          }
          to {
            height: 100%;
          }
        }
      `}</style>
    </section>
  );
}
